import { ApiError } from 'utils/api';
import { datadogTrackEvent, logError } from 'utils/datadog';
import { GuestRedeemState } from './GuestSessionContext';

/** Map a failed guest request to the redeem/terminal state that should render. */
export const stateFromError = (error: unknown): GuestRedeemState => {
  const code = error instanceof ApiError ? error.code : undefined;
  switch (code) {
    case 'error.access-grant.already-submitted':
      return 'submitted';
    case 'error.access-grant.expired':
      return 'expired';
    case 'error.guest.unavailable':
      return 'unavailable';
    default:
      // error.access-grant.invalid, error.guest.session-expired, unknown → invalid link
      return 'invalid';
  }
};

/**
 * Outcomes that are a normal part of the link lifecycle — reopened after submitting,
 * the 7-day grant window over, the 60-min session lapsed. They render a friendly
 * screen, so they are tracked as RUM actions, never as errors.
 * `error.access-grant.invalid` is deliberately NOT here: a link that should work but
 * doesn't is the one grant failure that must stay loud in Error Tracking.
 */
const EXPECTED_GRANT_CODES = new Set([
  'error.access-grant.already-submitted',
  'error.access-grant.expired',
  'error.guest.session-expired'
]);

/**
 * Codes after which the grant is dead for good: no write can ever succeed again, so the
 * caller must stop (autosave loop, submit retry) and swap to the matching terminal screen.
 * `error.guest.session-expired` is NOT terminal — the session lapses while the grant
 * stays valid, and recoverSession re-redeems it.
 */
const TERMINAL_GRANT_CODES = new Set([
  'error.access-grant.already-submitted',
  'error.access-grant.expired',
  'error.access-grant.invalid'
]);

export const isTerminalGrantError = (error: unknown): error is ApiError =>
  error instanceof ApiError && TERMINAL_GRANT_CODES.has(error.code);

export type GrantErrorStage = 'redeem' | 'recovery' | 'autosave' | 'submit';

/**
 * Report a failed guest request to Datadog: an expected lifecycle outcome becomes a
 * countable RUM action (product keeps the reopen-rate signal), anything else — invalid
 * grant, unknown code, network failure — stays a RUM error.
 */
export const reportGrantOutcome = (
  stage: GrantErrorStage,
  error: unknown
): void => {
  const code = error instanceof ApiError ? error.code : undefined;
  if (code && EXPECTED_GRANT_CODES.has(code)) {
    datadogTrackEvent('guest_cv_grant_outcome', { stage, code });
    return;
  }
  logError(`Guest CV ${stage} failed`, error);
};
